import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShieldCheck } from "lucide-react";
import { api, apiError, fmtDate } from "../../lib/api";
import { toast } from "sonner";
import { localeOf } from "../../i18n";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";

/** Super-admin only: system-level overview and role audit trail. */
export default function SystemConsole() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [activity, setActivity] = useState([]);
  const [users, setUsers] = useState([]);
  const locale = localeOf(i18n.language);

  const load = () => {
    api.get("/system/overview").then(({ data }) => setData(data)).catch(() => {});
    api.get("/system/role-audit").then(({ data }) => setAudit(data)).catch(() => {});
    api.get("/system/activity", { params: { limit: 100 } })
      .then(({ data }) => setActivity(data)).catch(() => {});
    api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const setSuper = async (userId, grant) => {
    try {
      await api.post("/system/super-admins", { user_id: userId, grant });
      toast.success(t("common.saved"));
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <p className="text-sm text-muted-foreground">{t("common.loading")}</p>;

  const candidates = users.filter(
    (u) => u.role === "admin" && u.is_active !== false && u.id !== user?.id);
  const supers = users.filter((u) => u.role === "super_admin");

  return (
    <div className="space-y-6" data-testid="system-console-page">
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-7 w-7 text-primary" />
        <h1 className="font-display text-3xl font-extrabold">{t("nav.system")}</h1>
      </div>

      <div className="vy-card p-5" data-testid="system-owner-card">
        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
          {t("admin.super_admin")}
        </p>
        <p className="mt-2 font-display text-xl font-bold">{user?.name}</p>
        <p className="text-sm text-muted-foreground">{user?.email}</p>
        <p className="mt-2 text-xs font-semibold text-muted-foreground">
          {t("system.activeSupers")}: {data.super_admins}
        </p>
      </div>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Object.entries(data.counts).map(([key, value]) => (
          <div key={key} className="vy-card p-5" data-testid={`system-count-${key}`}>
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{key}</p>
            <p className="mt-2 font-display text-3xl font-extrabold">{value}</p>
          </div>
        ))}
      </section>

      <section className="vy-card p-5" data-testid="system-roles-card">
        <h2 className="font-display text-lg font-bold">{t("admin.role")}</h2>
        <ul className="mt-3 divide-y divide-border">
          {Object.entries(data.users_by_role).map(([role, count]) => (
            <li key={role} className="flex items-center justify-between py-3 text-sm">
              <span className="font-semibold">{t(`admin.${role}`, role)}</span>
              <span className="font-display text-base font-bold">{count}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="vy-card p-5" data-testid="system-nominate-card">
        <h2 className="font-display text-lg font-bold">{t("system.nominate")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t("system.nominateLead")}</p>
        <ul className="mt-4 divide-y divide-border">
          {supers.map((s) => (
            <li key={s.id} className="flex items-center gap-3 py-3" data-testid={`super-row-${s.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{s.name}</p>
                <p className="text-xs text-muted-foreground">{s.email}</p>
              </div>
              {s.id !== user?.id && (
                <Button data-testid={`revoke-super-btn-${s.id}`} variant="outline"
                  className="h-10 rounded-full text-xs font-bold text-destructive"
                  onClick={() => setSuper(s.id, false)}>{t("system.revoke")}</Button>
              )}
            </li>
          ))}
          {candidates.map((c) => (
            <li key={c.id} className="flex items-center gap-3 py-3" data-testid={`candidate-row-${c.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{c.name}</p>
                <p className="text-xs text-muted-foreground">
                  {c.email} · {t(`admin.${c.role}`, c.role)}
                </p>
              </div>
              <Button data-testid={`grant-super-btn-${c.id}`} variant="outline"
                className="h-10 rounded-full text-xs font-bold"
                onClick={() => setSuper(c.id, true)}>{t("system.grant")}</Button>
            </li>
          ))}
          {candidates.length === 0 && supers.length === 0 && (
            <li className="py-3 text-sm text-muted-foreground">{t("common.none")}</li>
          )}
        </ul>
      </section>

      <section className="vy-card overflow-x-auto p-5" data-testid="system-activity-card">
        <h2 className="font-display text-lg font-bold">{t("system.activity")}</h2>
        <table className="mt-3 w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-muted-foreground">
              <th className="py-2">{t("admin.name")}</th>
              <th className="py-2">{t("common.actions")}</th>
              <th className="py-2">{t("admin.role")}</th>
              <th className="py-2">{t("profile.history")}</th>
            </tr>
          </thead>
          <tbody>
            {activity.map((a) => (
              <tr key={a.id} className="border-t border-border" data-testid={`activity-row-${a.id}`}>
                <td className="py-3 font-semibold">{a.actor_email || "—"}</td>
                <td className="py-3 text-muted-foreground">
                  {a.action}
                  <span className="ml-2 text-xs">{a.method} {a.path} · {a.status}</span>
                </td>
                <td className="py-3 text-xs">{a.actor_role ? t(`admin.${a.actor_role}`, a.actor_role) : "—"}</td>
                <td className="py-3 text-xs text-muted-foreground">{fmtDate(a.created_at, locale)}</td>
              </tr>
            ))}
            {activity.length === 0 && (
              <tr><td colSpan={4} className="py-4 text-muted-foreground">{t("common.none")}</td></tr>
            )}
          </tbody>
        </table>
      </section>

      <section className="vy-card overflow-x-auto p-5" data-testid="system-audit-card">
        <h2 className="font-display text-lg font-bold">{t("system.roleAudit")}</h2>
        <table className="mt-3 w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-muted-foreground">
              <th className="py-2">{t("auth.email")}</th>
              <th className="py-2">{t("admin.role")}</th>
              <th className="py-2">{t("common.actions")}</th>
              <th className="py-2">{t("profile.history")}</th>
            </tr>
          </thead>
          <tbody>
            {audit.map((a) => (
              <tr key={a.id} className="border-t border-border" data-testid={`audit-row-${a.id}`}>
                <td className="py-3 font-semibold">{a.target_email}</td>
                <td className="py-3 text-muted-foreground">
                  {a.previous_role || "—"} → {a.changes?.role || a.previous_role || "—"}
                </td>
                <td className="py-3 text-xs text-muted-foreground">{a.actor_email}</td>
                <td className="py-3 text-xs text-muted-foreground">{fmtDate(a.created_at, locale)}</td>
              </tr>
            ))}
            {audit.length === 0 && (
              <tr><td colSpan={4} className="py-4 text-muted-foreground">{t("common.none")}</td></tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
